/* eslint-disable no-param-reassign */
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer, CustomerAccount } from '@inyova/models';
import { cloneDeep } from 'lodash-es';
import { PageScrollService } from 'ngx-page-scroll-core';

import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { RiskQuestionsSurveyOption, RiskSurveyQuestion } from '@shared/models/Strategy';
import { TrackingService } from '@shared/services/tracking.service';
import { getAccAge, getExistingInvestment, getInvestmentDuration, getInvestmentPercentage } from '@shared/utils';
import { isGeneralRiskQuestionAndPrefilled } from '@strategy/strategy.utils';
import { RISK_CONSTANTS } from 'src/app/app.constants';

@Component({
  selector: 'app-strategy-ch-risk',
  styleUrls: ['./strategy-ch-risk.component.scss'],
  templateUrl: './strategy-ch-risk.component.html',
  standalone: false
})
export class StrategyCHRiskComponent implements OnDestroy, OnInit {
  @Input() recheck = false;
  @ViewChild('sliderWrapper') sliderWrapper: ElementRef;
  protected readonly onDestroy$ = new Subject<void>();

  bondRatio: number;
  currentAccount: CustomerAccount;
  customer: Customer;
  enableSaveButton = false;
  increasedRiskAcknowledgement: boolean;
  isLoading: boolean;
  pickedValue: number;
  recommendedClass: string;
  RISK_CONSTANTS = RISK_CONSTANTS;
  riskName = 'balanced';
  riskSurveyQuestionsInitial: RiskSurveyQuestion[];
  riskSurveyQuestions: RiskSurveyQuestion[];
  shortInvestmentAgreed = true;
  showShortInvestmentAgreed = false;
  showIncreasedRiskmMessage: boolean;
  stockRatio: number;
  stockSplit: number;
  showStockSplitSlider = false;
  stockSplitPicked: number;
  wasChanged = false;
  riskAnswersCopy = [];
  startAmplitudeSubmitted: boolean;
  savedRiskAnswers = [];
  initialInvestment = 0;
  monthlyInvestment = 0;
  knowledgeQuestion: { questionID?: number; optionExperiencedID?: number; optionBeginnerID?: number };
  shouldHidePrefilledQuestions = true;
  visibleRiskSurveyQuestions = [] as RiskSurveyQuestion[];

  constructor(
    private router: Router,
    private store: Store,
    private trackingService: TrackingService,
    private scrollService: PageScrollService,
    private amplitudeService: AmplitudeService,
    @Inject(DOCUMENT) private document: any
  ) {
    this.store
      .pipe(
        select(fromAppRoot.selectLanguageChange),
        filter((isLanguageChange) => isLanguageChange === true),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.store.dispatch(StrategyActions.getRiskStockSplitSuccess({ riskStockSplit: null, maxStockSplit: null }));
        this.store.dispatch(StrategyActions.getRiskQuestions());
        this.store.dispatch(AppRootActions.setIsLanguageChange({ change: false }));
      });

    this.store.pipe(select(fromAppRoot.selectCurrentInvestAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentAccount = value;
    });

    this.store.pipe(select(fromStrategy.selectRiskLoader), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.isLoading = value;
    });

    this.store.pipe(select(fromStrategy.selectCHHiddenRiskQuestion), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.knowledgeQuestion = value;
    });

    this.store.pipe(select(fromStrategy.selectCustomerInvestmentData), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.initialInvestment = value.total;
      this.monthlyInvestment = value.monthly_investment;
    });

    this.store.pipe(select(fromStrategy.selectRiskStockSplit), takeUntil(this.onDestroy$)).subscribe(({ riskStockSplit, maxStockSplit }) => {
      if (riskStockSplit === null) {
        return;
      }
      const possibleAnswers = [0.1, 0.3, 0.5, 0.7, 0.9];
      let finalStockSplit = riskStockSplit;
      if (possibleAnswers.includes(riskStockSplit)) {
        finalStockSplit = Number((riskStockSplit + 0.1).toFixed(1));
      }
      this.stockSplit = finalStockSplit;

      this.getRecommendedClass(this.stockSplit);
      this.getRiskName();

      if (this.riskSurveyQuestions && this.riskSurveyQuestions.length && this.wasChanged) {
        const willingnessQuestion = this.riskSurveyQuestions.find((item) => item.inyova_id === RISK_CONSTANTS.SLIDER_QUESTION);
        if (willingnessQuestion) {
          const answered = willingnessQuestion.survey_answer_options.find((item) => item.weight === this.stockSplit);
          this.updateWillingnessQuestion({ target: { value: (answered.score + 2).toString() } });
        }
      }
    });

    this.store
      .pipe(
        select(fromStrategy.selectCHRiskIsInitialized),
        filter((isInitialized) => isInitialized === false),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.store.dispatch(StrategyActions.getRiskStockSplitSuccess({ riskStockSplit: null, maxStockSplit: null }));
        this.store.dispatch(StrategyActions.getRiskQuestions());
      });

    combineLatest([this.store.pipe(select(fromAppRoot.selectCustomer)), this.store.pipe(select(fromStrategy.selectCHRiskQuestions))])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([customer, riskQuestions]) => {
        this.customer = customer;
        this.shouldHidePrefilledQuestions = customer.qualification_step === 'completed' && !this.recheck;
        if (!riskQuestions.length) {
          return;
        }

        this.riskSurveyQuestionsInitial = riskQuestions;
        this.riskSurveyQuestions = cloneDeep(riskQuestions);
        this.visibleRiskSurveyQuestions = this.getVisibleRiskSurveyQuestions(riskQuestions, this.shouldHidePrefilledQuestions);

        // Update single choice questions
        this.riskSurveyQuestions.forEach((item) => this.initialiseAnswersAfterLoad(item));
        // Make a copy of surevy answers
        this.riskAnswersCopy = [...this.getRiskAnswers()];
        // make a saved answers id for showing original answers
        this.savedRiskAnswers = this.riskAnswersCopy.map((answer) => answer.answer);

        // Update willingenss question
        const willingnessQuestionIndex = this.riskSurveyQuestions.findIndex((item) => item.inyova_id === RISK_CONSTANTS.SLIDER_QUESTION);
        if (willingnessQuestionIndex >= 0 && this.riskSurveyQuestions[willingnessQuestionIndex]?.survey_customer_answer?.answer) {
          const answered = this.riskSurveyQuestions[willingnessQuestionIndex].survey_answer_options.find(
            (item) => item.id === this.riskSurveyQuestions[willingnessQuestionIndex].survey_customer_answer.answer
          );
          this.updateWillingnessQuestion({ target: { value: (answered.score + 2).toString() } }, true);
        }

        const shortInvestmentQuestion = this.riskSurveyQuestions.find((item) => item.inyova_id === RISK_CONSTANTS.SHORT_INVESTMENT.questionInyovaID);

        // if the user has an answer to the first question we can assume the shortInvestment is accepted
        if (shortInvestmentQuestion.survey_customer_answer) {
          this.shortInvestmentAgreed = true;
          shortInvestmentQuestion.hasAnswer = true;
        }

        const filtered = this.riskSurveyQuestions.filter((item) => item.inyova_id !== RISK_CONSTANTS.SLIDER_QUESTION);
        this.showStockSplitSlider = !filtered.some((item) => !item.survey_customer_answer || item.survey_customer_answer.answer === null);
        this.getRiskStockSplit();
        this.enableSaveButtonCheck();

        this.trackRiskInitialisation(this.savedRiskAnswers.length > 0);
      });
  }

  getVisibleRiskSurveyQuestions(allQuestions: RiskSurveyQuestion[], shouldHidePrefilledQuestions: boolean): RiskSurveyQuestion[] {
    return allQuestions.filter((question) => {
      if (question.inyova_id === RISK_CONSTANTS.SLIDER_QUESTION) {
        return false;
      }

      if (!shouldHidePrefilledQuestions) {
        return true;
      }

      return !isGeneralRiskQuestionAndPrefilled(question);
    });
  }

  trackTooltip(name: string) {
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: name,
      screen: 'CH risk',
      consultation_method: 'hover'
    });
  }

  showQuestion(question: RiskSurveyQuestion): boolean {
    return this.visibleRiskSurveyQuestions.some((q) => q.inyova_id === question.inyova_id);
  }

  answerQuestion(question: RiskSurveyQuestion, answer: RiskQuestionsSurveyOption): void {
    const questionCopy = {
      ...question,
      hasAnswer: true,
      question_text: this.replaceQuestionText(answer),
      survey_customer_answer: {
        answer: answer.id
      }
    };
    const index = this.riskSurveyQuestions.findIndex((item) => item.id === question.id);

    if (question.inyova_id === RISK_CONSTANTS.SHORT_INVESTMENT.questionInyovaID) {
      if (answer.inyova_id === RISK_CONSTANTS.SHORT_INVESTMENT.optionInyovaID) {
        this.showShortInvestmentAgreed = true;
        this.shortInvestmentAgreed = false;
        questionCopy.hasAnswer = false;
      } else {
        this.showShortInvestmentAgreed = false;
        this.shortInvestmentAgreed = true;
      }
    }

    this.riskSurveyQuestions[index] = questionCopy;

    const filtered = this.riskSurveyQuestions.filter((item) => item.inyova_id !== RISK_CONSTANTS.SLIDER_QUESTION);
    this.showStockSplitSlider = !filtered.some((item) => !item.survey_customer_answer || item.survey_customer_answer.answer === null);
    this.enableSaveButtonCheck();
    this.getRiskStockSplit();

    if (this.showStockSplitSlider) {
      setTimeout(() => {
        if (this.sliderWrapper && !this.recheck) {
          this.scrollService.scroll({
            document: this.document,
            scrollTarget: this.sliderWrapper.nativeElement
          });
        }
      }, 300);
    }
  }

  enableSaveButtonCheck(): void {
    this.enableSaveButton = !this.riskSurveyQuestions.some((item) => !item.survey_customer_answer || item.survey_customer_answer.answer === null);
    if (this.showIncreasedRiskmMessage && this.showIncreasedRiskmMessage) {
      this.enableSaveButton = this.enableSaveButton && this.increasedRiskAcknowledgement;
    }
    this.enableSaveButton = this.enableSaveButton && this.shortInvestmentAgreed;
    this.wasChanged = JSON.stringify(this.getRiskAnswers()) !== JSON.stringify(this.riskAnswersCopy);
    this.store.dispatch(StrategyActions.setStrategyWasChanged({ strategyWasChanged: this.wasChanged, modalOpened: false, redirectUrl: '' }));
  }

  ngOnInit(): void {
    this.startAmplitudeSubmitted = false;
    if (this.recheck) {
      this.trackingService.trackView('/risk-recheck');
    } else {
      this.trackingService.trackView('/risk-profile');
    }
  }

  cancelChanges(): void {
    this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: 5 }));
  }

  send(continueToPortfolio = false): void {
    if (!this.wasChanged) {
      this.cancelChanges();
    }

    const payload = {
      one_time_investment: this.initialInvestment,
      period_investment: this.monthlyInvestment,
      risk_answers: this.getRiskAnswers()
    };

    if (this.knowledgeQuestion && this.knowledgeQuestion.questionID) {
      payload.risk_answers.push({
        question_id: this.knowledgeQuestion.questionID,
        answer: this.stockSplitPicked > this.stockSplit ? this.knowledgeQuestion.optionExperiencedID : this.knowledgeQuestion.optionBeginnerID
      });
    }

    this.store.dispatch(StrategyActions.updateRisk({ continueToPortfolio, updateObject: payload }));
  }

  updateWillingnessQuestion(event: { target: { value: string } }, firstTimeInit = false): void {
    const { value } = event.target as HTMLInputElement;
    const willingnessQuestionIndex = this.riskSurveyQuestions.findIndex((item) => item.inyova_id === 5);
    const answered = this.riskSurveyQuestions[willingnessQuestionIndex].survey_answer_options[value] as RiskQuestionsSurveyOption;
    const questionCopy = {
      ...this.riskSurveyQuestions[willingnessQuestionIndex],
      survey_customer_answer: {
        answer: answered.id
      }
    };
    this.pickedValue = answered.score + 2;
    this.stockSplitPicked = answered.weight;
    this.bondRatio = (1 - answered.weight) * 100;
    this.stockRatio = answered.weight * 100;
    this.riskSurveyQuestions[willingnessQuestionIndex] = questionCopy;
    if (!firstTimeInit) {
      this.showIncreasedRiskmMessage = this.stockSplitPicked > this.stockSplit;
      this.enableSaveButtonCheck();
    }
    this.getRiskName();
  }

  getRiskName(): void {
    switch (true) {
      case this.stockSplitPicked >= 1:
        this.riskName = 'very_dynamic';
        break;
      case this.stockSplitPicked >= 0.8:
        this.riskName = 'dynamic';
        break;
      case this.stockSplitPicked >= 0.6:
        this.riskName = 'balanced';
        break;
      case this.stockSplitPicked >= 0.4:
        this.riskName = 'conservative';
        break;
      case this.stockSplitPicked >= 0.2:
        this.riskName = 'very_conservative';
        break;
      default:
        this.riskName = 'balanced';
        break;
    }
  }

  getRecommendedClass(stockSplit: number): void {
    switch (true) {
      case stockSplit >= 1:
        this.recommendedClass = 'very_dynamic';
        break;
      case stockSplit >= 0.8:
        this.recommendedClass = 'dynamic';
        break;
      case stockSplit >= 0.6:
        this.recommendedClass = 'balanced';
        break;
      case stockSplit >= 0.4:
        this.recommendedClass = 'conservative';
        break;
      case stockSplit >= 0.2:
        this.recommendedClass = 'very_conservative';
        break;
      default:
        this.recommendedClass = 'active';
        break;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.startAmplitudeSubmitted = false;
  }

  private initialiseAnswersAfterLoad(question: RiskSurveyQuestion) {
    if (
      question &&
      question.survey_customer_answer &&
      question.survey_customer_answer.answer &&
      question.inyova_id !== RISK_CONSTANTS.SLIDER_QUESTION
    ) {
      const answer = question.survey_answer_options.find((questionItem) => questionItem.id === question.survey_customer_answer.answer);
      question.hasAnswer = true;
      if (answer) {
        question.question_text = this.replaceQuestionText(answer);
      }
    }
  }

  private getRiskAnswers(): { question_id: number; answer: number | string }[] {
    const riskAnswers: { question_id: number; answer: number | string }[] = [];
    this.riskSurveyQuestions.forEach((question: RiskSurveyQuestion) => {
      if (question && question.survey_customer_answer && question.survey_customer_answer.answer) {
        riskAnswers.push({
          question_id: question.id,
          answer: question.survey_customer_answer.answer as string
        });
      }
    });
    return riskAnswers;
  }

  private getRiskStockSplit(): void {
    const riskAnswers = this.getRiskAnswers();
    if (riskAnswers && riskAnswers.length >= 4) {
      this.store.dispatch(StrategyActions.getRiskStockSplit({ riskAnswers }));
    }
  }

  private replaceQuestionText(answer: RiskQuestionsSurveyOption): string {
    return answer.details;
  }

  updateShortInvestment(): void {
    const shortInvestmentQuestion = this.riskSurveyQuestions.find((item) => item.inyova_id === RISK_CONSTANTS.SHORT_INVESTMENT.questionInyovaID);

    // update answer according to 5 years answer - it returns id instead of inyova_id // weirdo - 3b - 9 // kid - 79
    const ANSWER_ID = this.currentAccount.kind === '3b' ? 9 : 79;

    if (shortInvestmentQuestion.survey_customer_answer) {
      this.shortInvestmentAgreed = shortInvestmentQuestion.survey_customer_answer.answer === ANSWER_ID;
      this.showShortInvestmentAgreed = false;
      shortInvestmentQuestion.hasAnswer = true;
    } else {
      this.showShortInvestmentAgreed = true;
      this.shortInvestmentAgreed = false;
    }

    this.enableSaveButtonCheck();
  }

  sendRecheck(): void {
    this.trackingService.trackActivity('Risk Recheck: [Button] Save changes', true);
    // this.send();
    const riskAnswers = this.getRiskAnswers();

    if (this.knowledgeQuestion && this.knowledgeQuestion.questionID) {
      riskAnswers.push({
        question_id: this.knowledgeQuestion.questionID,
        answer: this.stockSplitPicked > this.stockSplit ? this.knowledgeQuestion.optionExperiencedID : this.knowledgeQuestion.optionBeginnerID
      });
    }

    this.store.dispatch(StrategyActions.updateRiskRecheck({ risk_answers: riskAnswers }));
  }

  discard(): void {
    void this.router.navigate(['/']);
    this.trackingService.trackActivity('Risk Recheck: [Button] Discard changes', true);
  }

  trackRiskInitialisation(hasAnswers = false) {
    if (this.startAmplitudeSubmitted) {
      return;
    }

    const payload = {
      placement: this.currentAccount.step_status < 5 ? 'onboarding' : 'strategy_configuration',
      offering_account_type: this.currentAccount.kind,
      investment_duration: hasAnswers ? getInvestmentDuration(this.riskAnswersCopy) : null,
      age_at: hasAnswers ? getAccAge(this.riskAnswersCopy) : null,
      investment_percentage: hasAnswers ? getInvestmentPercentage(this.riskAnswersCopy) : null,
      existing_investments: hasAnswers ? getExistingInvestment(this.riskSurveyQuestions, 9) : null
    };
    this.startAmplitudeSubmitted = true;

    this.amplitudeService.trackEvent('Risk Profile Adjust Started', payload);
  }
}
